'use client';

import React, { useCallback, useEffect, useRef, useState } from 'react';
import type { InquiryFlowSnapshot } from '@miso/design-system';
import type { VisitCounselView } from '@/entities/visit-counsel/model/visit-counsel.types';
import type { ProgressSubStage } from '@/features/visit-counsel/counsel-flow-stage/model/resolveCounselFlowStage';
import { MovingTypesFlowAdapter } from '@/features/visit-counsel/moving-types-flow';
import { MovingDateStepAdapter } from './MovingDateStepAdapter';
import { DepartureTransportStepAdapter } from './DepartureTransportStepAdapter';
import { ArrivalAddressStepAdapter } from './ArrivalAddressStepAdapter';
import { ArrivalTransportStepAdapter } from './ArrivalTransportStepAdapter';
import { PackingStepAdapter } from './PackingStepAdapter';
import { CustomerRequestsStepAdapter } from './CustomerRequestsStepAdapter';

type ProgressSubStageKind = ProgressSubStage['kind'];

/**
 * 7 sub-stage 의 진행 순서. 뒤로가기 시 "이전 sub-stage" 결정 + back-stack 의
 * 자연스러운 진행을 보장한다 (Figma 시퀀스 그대로).
 *
 * `completed` 는 stage='done' 으로 분기되므로 본 컴포넌트가 렌더하지 않는다.
 */
const SUB_STAGE_ORDER: ProgressSubStageKind[] = [
  'moving-date',
  'departure-transport',
  'arrival-address',
  'arrival-transport',
  'packing',
  'moving-types',
  'customer-requests',
];

/** global progress bar 계산용 예상 질문 수. */
const ESTIMATED_Q_COUNTS: Record<string, number> = {
  'moving-date': 1,
  'departure-transport': 3,
  'arrival-address': 4,
  'arrival-transport': 3,
  packing: 1,
  'moving-types': 1,
  'customer-requests': 1,
};

function getPreviousSubStageKind(kind: ProgressSubStageKind): ProgressSubStageKind | null {
  const i = SUB_STAGE_ORDER.indexOf(kind);
  if (i <= 0) return null;
  return SUB_STAGE_ORDER[i - 1];
}

export interface ProgressFlowAdapterProps {
  visitCounselId: string;
  counsel: VisitCounselView;
  /** ProgressSubStage 결정 결과 — CounselStartPage 가 resolveCounselFlowStage 로 받아 전달 */
  sub: ProgressSubStage;
  /**
   * 신청 시 고객이 입력한 이사일 (`order.moving.moveDate`).
   *
   * `counsel.movingDate` 가 아직 기록되지 않은 경우(첫 진입) `MovingDateStepAdapter` 의
   * 날짜 입력창에 이 값을 기본값으로 채워준다 — 상담사가 별도 확인 없이 바로 다음으로
   * 진행할 수 있도록 UX를 개선한다.
   */
  orderMoveDate?: string | null;
  showToast: (message: string, severity?: 'success' | 'error' | 'warning' | 'neutral') => void;
  refetch: () => Promise<{ counsel?: VisitCounselView | null } | undefined>;
  onAfterDone: (counsel: VisitCounselView) => void;
  onCancel: () => void;
}

/**
 * "상담 진행하기" sub-stage 디스패처.
 *
 * counsel 스냅샷 기반의 sub-stage 결정 (`props.sub`) 을 기본으로 따르되, 사용자가
 * 첫 질문에서 뒤로가기 했을 때 **이전 sub-stage 의 마지막 질문** 으로 이동시키는
 * back-stack 동작을 내부에 유지한다.
 *
 * back-stack 메커니즘 (Option B + startAtQuestionId)
 * ---------------------------------------------------
 * 사용자 핵심 요구 "InquiryFlow 하나로 엮어서 이전버튼 누를때 이전질문으로" 를
 * 위해 우선 옵션 A (단일 통합 FlowDocument + design-system 의 reducer 에 cross-step
 * back 추가) 를 검토했으나:
 *
 *   1) `useFlowReducer` 가 step 진입 시 currentIndex=0 으로 시작하도록 설계돼
 *      있어, 첫 질문이 아닌 "마지막 visible 질문" 으로 hydrate 하려면 reducer
 *      action / state shape (`stepHistory` stack, `entryMode: 'tail'` 등) 을
 *      확장해야 한다. 이는 design-system 의 다른 사용처(playground 등) 및 267 개
 *      vitest 전반에 회귀 위험을 동반한다.
 *   2) `MovingTypesFlowAdapter` 의 item-picker 는 multi-instance sub-flow + intake
 *      API 호출 (`recordMovingTypeIntake`) 가 별도이며 통합 FlowDocument 의 한
 *      step 으로 흡수하기에는 sub-flow 라이프사이클이 부조화한다.
 *   3) 각 stage 의 `record*` API 호출 + counsel refetch + skipIf cascade prefill
 *      을 **step 전환 hook** 으로 평탄화하면 instance summary / 옵션 builder /
 *      switchTo variant 등 기존 design-system 기능과의 결합도가 더 커진다.
 *
 * 결론적으로 사용자 핵심 요구는 "뒤로가기 → 이전 질문 이동" 이므로 design-system
 * 손대지 않고 각 sub-stage 의 첫 질문에서 onCancel 을 가로채 **이전 sub-stage 를
 * `startAtLast=true` 로 재마운트** 하는 방식을 택했다. 사용자 체감상은 "InquiryFlow
 * 하나처럼" 동작하면서, 회귀 위험은 progress-flow / moving-types-flow 어댑터에
 * 한정된다.
 *
 * back-stack 상태 전이
 *   - `backOverride` 가 set → 해당 sub-stage 를 `startAtLast={true}` 로 렌더.
 *   - sub-stage 의 onCancel(첫 질문에서 뒤로가기) 발생:
 *       previous 가 있으면 `backOverride` 를 previous 로 갱신.
 *       previous 가 없으면 (= moving-date) 부모의 onCancel 호출 (counsel detail).
 *   - sub-stage 가 완료(onAfterDone) 되면 `backOverride` 를 해제한다.
 *     counsel refetch 결과로 sub prop 이 갱신되면 CounselStartPage 의 key 가 변경되어
 *     본 컴포넌트가 자연스러운 다음 sub-stage 로 진입한다.
 *   - sub prop 의 kind 가 바뀌면 (counsel 변화) override 가 stale 일 수 있으므로
 *     useEffect 에서 자동 리셋한다.
 *
 * 매핑 (Figma 시퀀스 → 어댑터):
 *   moving-date         → MovingDateStepAdapter        (recordMovingDate)
 *   departure-transport → DepartureTransportStepAdapter (recordDepartureSite)
 *   arrival-address     → ArrivalAddressStepAdapter     (recordArrivalSite — 주소만)
 *   arrival-transport   → ArrivalTransportStepAdapter   (recordArrivalSite — 운반방식 갱신)
 *   packing             → PackingStepAdapter            (recordPacking)
 *   moving-types        → MovingTypesFlowAdapter        (recordMovingTypeIntake)
 *   customer-requests   → CustomerRequestsStepAdapter   (recordCustomerRequests)
 *   completed           → resolver 가 stage='done' 으로 변경 → 이 컴포넌트는 마운트되지 않음.
 */
export function ProgressFlowAdapter(props: ProgressFlowAdapterProps) {
  const {
    sub,
    onCancel: outerOnCancel,
    onAfterDone: outerOnAfterDone,
    orderMoveDate,
    ...rest
  } = props;

  /**
   * 뒤로가기로 강제 렌더 중인 sub-stage. null 이면 props.sub 를 그대로 따른다.
   * 항상 props.sub.kind 보다 앞선 stage 만 들어간다 (`getPreviousSubStageKind` 결과).
   */
  const [backOverrideKind, setBackOverrideKind] = useState<ProgressSubStageKind | null>(null);

  /** 완료된 각 stage 의 실제 질문 수 누적 배열. */
  const [completedQCounts, setCompletedQCounts] = useState<number[]>([]);
  /** 현재 stage 의 실제 질문 수 (InquiryFlow snapshot 에서 갱신). */
  const [currentStageTotal, setCurrentStageTotal] = useState<number>(1);

  // sub.kind 가 변하면 (counsel refetch 로 자연스럽게 진행됨) override 를 항상 리셋.
  // 같은 sub.kind 안에서의 back-stack 진입은 이 effect 가 영향 주지 않는다.
  const prevSubKindRef = useRef(sub.kind);
  useEffect(() => {
    if (prevSubKindRef.current !== sub.kind) {
      prevSubKindRef.current = sub.kind;
      if (backOverrideKind !== null) setBackOverrideKind(null);
    }
  }, [sub.kind, backOverrideKind]);

  // override 가 set 인 경우 그 stage 를 보여준다.
  const renderedKind: ProgressSubStageKind = backOverrideKind ?? sub.kind;
  // override 활성 = 뒤로가기로 진입한 경우. 어댑터에 startAtLast 신호로 전달.
  const startAtLast = backOverrideKind !== null;

  const handleStageCancel = useCallback(() => {
    const prev = getPreviousSubStageKind(renderedKind);
    if (prev !== null) {
      setBackOverrideKind(prev);
      return;
    }
    // 첫 sub-stage 까지 거슬러 올라간 경우 — 부모의 onCancel(counsel detail 이동) 위임.
    outerOnCancel();
  }, [renderedKind, outerOnCancel]);

  const handleAfterDone = useCallback(
    (updated: VisitCounselView) => {
      // 현재 stage 완료 → 실제 질문 수를 누적 배열에 추가.
      setCompletedQCounts((prev) => [...prev, currentStageTotal]);
      setCurrentStageTotal(1);
      // override 로 들어와 stage 를 완료한 경우 → override 해제.
      // 이후 counsel refetch 로 sub prop 이 갱신되거나, 같은 stage 가 다시 결정되더라도
      // 정상 진행 흐름을 따른다.
      if (backOverrideKind !== null) setBackOverrideKind(null);
      outerOnAfterDone(updated);
    },
    [backOverrideKind, currentStageTotal, outerOnAfterDone]
  );

  /** InquiryFlow snapshot 을 받아 현재 stage 의 실제 질문 수를 갱신한다. */
  const handleFlowStateChange = useCallback((snap: InquiryFlowSnapshot) => {
    const total = snap.step?.progress?.total ?? 1;
    setCurrentStageTotal(total);
  }, []);

  // completed 는 resolver 가 stage='done' 으로 분기하므로 정상 흐름에서는 도달 불가.
  if (renderedKind === 'completed') return null;

  // ── Global progress bar 계산 ──────────────────────────────────────────────
  const currentStageIdx = SUB_STAGE_ORDER.indexOf(
    renderedKind as Exclude<ProgressSubStageKind, 'completed'>
  );
  const progressOffset = completedQCounts.reduce((a, b) => a + b, 0);
  const remainingStages = SUB_STAGE_ORDER.slice(currentStageIdx + 1);
  const estimatedRemaining = remainingStages.reduce((s, k) => s + (ESTIMATED_Q_COUNTS[k] ?? 1), 0);
  const progressTotal = progressOffset + currentStageTotal + estimatedRemaining;

  const adapterProps = {
    ...rest,
    onCancel: handleStageCancel,
    onAfterDone: handleAfterDone,
    progressOffset,
    progressTotal,
    onStateChange: handleFlowStateChange,
  };

  switch (renderedKind) {
    case 'moving-date':
      return (
        <MovingDateStepAdapter
          {...adapterProps}
          startAtLast={startAtLast}
          orderMoveDate={orderMoveDate}
        />
      );
    case 'departure-transport':
      return <DepartureTransportStepAdapter {...adapterProps} startAtLast={startAtLast} />;
    case 'arrival-address':
      return <ArrivalAddressStepAdapter {...adapterProps} startAtLast={startAtLast} />;
    case 'arrival-transport':
      return <ArrivalTransportStepAdapter {...adapterProps} startAtLast={startAtLast} />;
    case 'packing':
      return <PackingStepAdapter {...adapterProps} startAtLast={startAtLast} />;
    case 'moving-types':
      return <MovingTypesFlowAdapter {...adapterProps} startAtLast={startAtLast} />;
    case 'customer-requests':
      return <CustomerRequestsStepAdapter {...adapterProps} />;
    default: {
      const exhaustiveCheck: never = renderedKind;
      void exhaustiveCheck;
      return null;
    }
  }
}
